"use client";

import type { ReactNode } from "react";
import {
  Controller,
  useFormContext,
  type ControllerProps,
} from "react-hook-form";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import InputAdornment from "@mui/material/InputAdornment";
import TextField, { type TextFieldProps } from "@mui/material/TextField";
import { FormField } from "./FormField";
import { formatPhoneNumberInput } from "@rmh/utils/src/phoneNumber";

export type FormPhoneFieldProps = {
  name: string;
  label?: ReactNode;
  helperText?: ReactNode;
  rules?: ControllerProps["rules"];
  /** Hides the label asterisk while keeping `required` on the input. */
  hideRequiredMarker?: boolean;
} & Omit<
  TextFieldProps,
  | "name"
  | "value"
  | "onChange"
  | "error"
  | "helperText"
  | "label"
  | "type"
  | "multiline"
>;

export const FormPhoneField = ({
  name,
  label,
  helperText,
  required,
  hideRequiredMarker,
  rules,
  ...rest
}: FormPhoneFieldProps) => {
  const { control } = useFormContext();
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => (
        <FormField
          label={label}
          helperText={fieldState.error?.message ?? helperText}
          error={!!fieldState.error}
          required={required}
          hideRequiredMarker={hideRequiredMarker}
          htmlFor={name}
        >
          <TextField
            id={name}
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            {...rest}
            name={field.name}
            onBlur={field.onBlur}
            inputRef={field.ref}
            value={field.value ?? ""}
            onChange={(e) =>
              field.onChange(formatPhoneNumberInput(e.target.value))
            }
            error={!!fieldState.error}
            required={required}
            slotProps={{
              ...rest.slotProps,
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIphoneOutlinedIcon
                      fontSize="small"
                      sx={(theme) => ({
                        color: theme.vars!.palette.rmh.icon.secondary,
                      })}
                    />
                  </InputAdornment>
                ),
                ...(rest.slotProps?.input ?? {}),
              },
            }}
          />
        </FormField>
      )}
    />
  );
};
